import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { StatusBadge } from "@/components/shared/status-badge";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ServiceForm } from "@/components/admin/billing/service-form";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Zap } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/format";
import type { ClientService, Client, Product } from "@db/schema";

type StatusFilter = "all" | "active" | "pending_payment" | "pending_cancellation" | "cancelled";

interface ServiceFormData {
  clientId: string;
  productId: string;
  contractId?: string;
  budgetId?: string;
  serviceTypeId?: string;
  defaultServiceItemId?: string;
  customName?: string;
  price?: string;
  recurrence: "monthly" | "annual";
  nextRenewalDate: string;
  coverageMode: (typeof import("@shared/contracts/billing").serviceCoverageModeValues)[number];
  collectionMethod: (typeof import("@shared/contracts/billing").serviceCollectionMethodValues)[number];
  notes?: string;
  initiallyPaid?: boolean;
  customFieldsData?: Record<string, string | boolean>;
}

interface ServicesResponse {
  services: (ClientService & { client: Client; product: Product })[];
  total: number;
  totalPages: number;
}

export default function ServicesPage() {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<ClientService | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<ClientService | null>(null);

  const { data: servicesData, isLoading } = useQuery<ServicesResponse>({
    queryKey: [`/api/billing/services?status=${statusFilter}&search=${search}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const services = servicesData?.services;

  const filteredServices = useMemo(() => {
    if (!services) return [];
    let result = services;

    if (statusFilter !== "all") {
      result = result.filter((s) => s.status === statusFilter);
    }

    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (s) =>
          (s.customName ?? "").toLowerCase().includes(term) ||
          s.notes?.toLowerCase().includes(term),
      );
    }

    return result;
  }, [services, search, statusFilter]);

  const createMutation = useMutation({
    mutationFn: async (data: ServiceFormData) => {
      const res = await apiRequest("POST", "/api/billing/client-services", data);
      return (await res.json()) as ClientService;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] as string)?.startsWith("/api/billing/") });
      setDialogOpen(false);
      toast({ title: "Service created successfully" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to create service",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: ServiceFormData;
    }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/billing/client-services/${id}`,
        data,
      );
      return (await res.json()) as ClientService;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] as string)?.startsWith("/api/billing/") });
      setDialogOpen(false);
      setEditingService(null);
      toast({ title: "Service updated successfully" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to update service",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/billing/client-services/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] as string)?.startsWith("/api/billing/") });
      setDeleteTarget(null);
      toast({ title: "Service deleted successfully" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to delete service",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function handleOpenCreate() {
    setEditingService(null);
    setDialogOpen(true);
  }

  function handleRowClick(service: ClientService) {
    setEditingService(service);
    setDialogOpen(true);
  }

  function handleFormSubmit(data: ServiceFormData) {
    const normalizedData: ServiceFormData = {
      ...data,
      contractId: data.contractId || undefined,
      budgetId: data.budgetId || undefined,
      serviceTypeId: data.serviceTypeId || undefined,
      defaultServiceItemId: data.defaultServiceItemId || undefined,
    };

    if (editingService) {
      updateMutation.mutate({ id: editingService.id, data: normalizedData });
    } else {
      createMutation.mutate(normalizedData);
    }
  }

  function getFormDefaults(service: ClientService | null) {
    if (!service) return undefined;
    return {
      clientId: service.clientId,
      productId: service.productId,
      contractId: (service as any).contractId ?? "",
      budgetId: (service as any).budgetId ?? "",
      serviceTypeId: (service as any).serviceTypeId ?? "",
      defaultServiceItemId: (service as any).defaultServiceItemId ?? "",
      customName: service.customName ?? "",
      price: service.price ?? "",
      recurrence: service.recurrence as "monthly" | "annual",
      nextRenewalDate: service.nextRenewalDate
        ? new Date(service.nextRenewalDate).toISOString().split("T")[0]
        : "",
      coverageMode: (service as any).coverageMode ?? "ad_hoc_only",
      collectionMethod: (service as any).collectionMethod ?? "stripe_invoice",
      notes: service.notes ?? "",
      customFieldsData: (service as any).customFieldsData ?? {},
    };
  }

  const columns: ColumnDef<ServicesResponse["services"][0]>[] = [
    {
      key: "name",
      header: "Service",
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {row.customName || row.product.name}
          </span>
          <span className="text-xs text-muted-foreground">
            {row.client.name}
          </span>
        </div>
      ),
    },
    {
      key: "price",
      header: "Price (ex. VAT)",
      cell: (row) =>
        row.price ? formatCurrency(row.price, row.currency) : "--",
    },
    {
      key: "recurrence",
      header: "Recurrence",
      cell: (row) => (
        <span className="capitalize">{row.recurrence}</span>
      ),
    },
    {
      key: "nextRenewalDate",
      header: "Renewal Date",
      cell: (row) => formatDate(row.nextRenewalDate),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => <StatusBadge type="service" status={row.status} />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Services"
        description="Manage client services and subscriptions"
        action={
          <Button onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Service
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={filteredServices}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        onRowClick={handleRowClick}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search services..."
        filterSlot={
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as StatusFilter)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending_payment">
                Pending Payment
              </SelectItem>
              <SelectItem value="pending_cancellation">
                Pending Cancellation
              </SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        }
        emptyIcon={Zap}
        emptyTitle="No services found"
        emptyDescription={
          search || statusFilter !== "all"
            ? "No services match your filters. Try adjusting your search."
            : "Get started by adding your first service."
        }
        emptyAction={
          !search && statusFilter === "all" ? (
            <Button onClick={handleOpenCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add Service
            </Button>
          ) : undefined
        }
      />

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false);
            setEditingService(null);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingService ? "Edit Service" : "Add Service"}
            </DialogTitle>
            <DialogDescription>
              {editingService
                ? "Update the service details below."
                : "Fill in the details to create a new service."}
            </DialogDescription>
          </DialogHeader>
          <ServiceForm
            key={editingService?.id ?? "new"}
            defaultValues={getFormDefaults(editingService)}
            onSubmit={handleFormSubmit}
            isPending={createMutation.isPending || updateMutation.isPending}
          />
          {editingService && (
            <div className="flex justify-start pt-2 border-t">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setDialogOpen(false);
                  setDeleteTarget(editingService);
                }}
              >
                Delete Service
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Service"
        description={`Are you sure you want to delete "${deleteTarget?.customName || "this service"}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() =>
          deleteTarget && deleteMutation.mutate(deleteTarget.id)
        }
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
